import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState } from '@ui-kit';
import { useTerminologySummary } from '../hooks/useTerminologySummary';
import { TerminologySummaryCards } from '../components/TerminologySummaryCards';
import { TerminologyDownloadMenu } from '../components/TerminologyDownloadMenu';
import { TerminologyToast, useToast } from '../components/TerminologyToast';
import type { ApiError } from '../types/terminology';

/* ─── Common Usage Panel (biz-foundation §4.11) ─────────────────────────── */
import { useState } from 'react';

function CommonUsagePanel() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const modules = [
    { id: 'C001', name: 'BusinessException', role: '도메인 예외 (TERM-404/409/403)' },
    { id: 'C002', name: 'ApiResponse<T> · PageResponse<T>', role: '모든 응답 래핑 · 페이지네이션' },
    { id: 'C006', name: 'RequestContext.userId()', role: '감사 actor 자동 (created_by/updated_by)' },
    { id: 'C007', name: 'CommonCodeService', role: 'TERMINOLOGY_STATUS 그룹 i18n 라벨' },
    { id: 'C008', name: 'EventPublisher.publish()', role: 'StandardTermChangedEvent 발행' },
    { id: 'C014', name: '@Audited(action="TERMINOLOGY_CHANGED")', role: '모든 mutation 자동 감사' },
    { id: 'C028', name: 'JwtAuthenticationService', role: 'Bearer 인증 (필터 자동)' },
    { id: 'C029', name: 'SecurityChainCustomizer', role: '/api/eraf/terminology/** authenticated()' },
    { id: 'C030', name: '@HasPermission("TERMINOLOGY_MANAGE")', role: 'POST/PUT/DELETE 메서드 권한 이중 방어' },
    { id: 'C039', name: 'AuditableEntity', role: '감사 6컬럼 자동 매핑' },
    { id: 'C040', name: 'SoftDelete', role: 'DELETE → is_deleted=true / status=DEPRECATED' },
    { id: 'C041', name: 'SpecificationBuilder', role: 'domain/source 필터 동적 쿼리' },
    { id: 'C042', name: 'GenericRepository<StandardTerm, Long>', role: '기본 CRUD 위임' },
    { id: 'C043', name: 'Flyway baseline', role: 'pg_trgm 확장 + DDL 마이그레이션' },
  ];

  return (
    <div
      style={{
        marginTop: 'var(--spacing-6)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-surface-muted)',
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls="terminology-common-usage"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          width: '100%',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-family-sans)',
          textAlign: 'left',
        }}
      >
        <span style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
        {t('terminologyManagement.commonUsage.title')}
      </button>
      {open && (
        <div
          id="terminology-common-usage"
          style={{ padding: 'var(--spacing-4)', borderTop: '1px solid var(--color-surface-border)' }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-xs)' }}>
            <thead>
              <tr>
                {['ID', 'API', '역할'].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-2)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {modules.map((m) => (
                <tr key={m.id}>
                  <td style={{ padding: 'var(--spacing-2)', borderBottom: '1px solid var(--color-surface-border)', fontFamily: 'var(--font-family-mono)', color: 'var(--color-brand-primary)' }}>{m.id}</td>
                  <td style={{ padding: 'var(--spacing-2)', borderBottom: '1px solid var(--color-surface-border)', fontFamily: 'var(--font-family-mono)', color: 'var(--color-text-primary)' }}>{m.name}</td>
                  <td style={{ padding: 'var(--spacing-2)', borderBottom: '1px solid var(--color-surface-border)', color: 'var(--color-text-secondary)' }}>{m.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────────── */

export function TerminologyDashboardPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const summaryQuery = useTerminologySummary();
  const { messages, addToast, dismissToast } = useToast();

  const { data, isLoading, isError, error, refetch } = summaryQuery;

  const lastCreatedAt = data?.recentTerms?.[0]?.createdAt
    ? new Intl.DateTimeFormat(i18n.language === 'en' ? 'en-US' : 'ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(data.recentTerms[0].createdAt))
    : '-';
  const lastCreatedBy = data?.recentTerms?.[0]?.createdBy ?? '-';

  return (
    <div style={{ padding: 'var(--spacing-6)', maxWidth: 1200, margin: '0 auto' }}>
      <PageHeader
        title={t('terminologyManagement.dashboard.title')}
        breadcrumb={<span>{t('terminologyManagement.dashboard.title')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <button
              type="button"
              data-testid="terminology-checker-nav"
              onClick={() => navigate('/admin/terminology/checker')}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
                color: 'var(--color-text-primary)',
              }}
            >
              {t('terminologyManagement.checker.navLabel')}
            </button>
            <TerminologyDownloadMenu
              onSuccess={(msg) => addToast(msg)}
              onError={(msg) => addToast(msg, 'error')}
            />
          </div>
        }
      />

      {/* Subtitle */}
      {data && (
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: 'var(--spacing-2) 0 var(--spacing-5)' }}>
          {t('terminologyManagement.dashboard.subtitle', {
            totalTerms: data.totalTerms.toLocaleString(),
            totalForbidden: data.totalForbidden.toLocaleString(),
            lastCreatedAt,
            lastCreatedBy,
          })}
        </p>
      )}

      {/* Error state */}
      {isError && (
        <ErrorState
          title={t('terminologyManagement.error.network')}
          message={(error as unknown as ApiError)?.message ?? t('terminologyManagement.error.network')}
          onRetry={() => void refetch()}
        />
      )}

      {/* Loading + Default: Summary Cards */}
      {!isError && (
        <TerminologySummaryCards summary={data} isLoading={isLoading} />
      )}

      {/* Recent 10 */}
      {!isError && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-3)' }}>
            <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('terminologyManagement.dashboard.section.recent')}
            </div>
            <button
              type="button"
              onClick={() => navigate('/admin/terminology/standard-terms')}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-brand-primary)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-family-sans)',
                fontWeight: 600,
              }}
            >
              {t('terminologyManagement.dashboard.link.allTerms')} →
            </button>
          </div>

          {/* Loading skeleton for recent list */}
          {isLoading && (
            <div
              aria-busy="true"
              style={{
                background: 'var(--color-surface-muted)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-surface-border)',
                padding: 'var(--spacing-4)',
                minHeight: 200,
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          )}

          {/* Default: recent term list */}
          {data && data.recentTerms.length > 0 && (
            <div
              style={{
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
              }}
              role="list"
              aria-label={t('terminologyManagement.dashboard.section.recent')}
            >
              {data.recentTerms.map((term, idx) => (
                <button
                  key={term.id}
                  type="button"
                  role="listitem"
                  onClick={() => navigate(`/admin/terminology/standard-terms/${term.id}`)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-3)',
                    width: '100%',
                    padding: 'var(--spacing-3) var(--spacing-4)',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: idx < data.recentTerms.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'var(--font-family-sans)',
                  }}
                >
                  <span style={{ fontWeight: 600, color: 'var(--color-text-primary)', fontSize: 'var(--font-size-sm)' }}>
                    {term.korean}
                  </span>
                  <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-mono)' }}>
                    ({term.englishShort})
                  </span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                      display: 'flex',
                      gap: 'var(--spacing-3)',
                      alignItems: 'center',
                    }}
                  >
                    <span>{term.source}</span>
                    <span>
                      {new Intl.DateTimeFormat(i18n.language === 'en' ? 'en-US' : 'ko-KR', {
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      }).format(new Date(term.createdAt))}
                    </span>
                    <span>{term.createdBy ?? '-'}</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Empty: if data loaded but recentTerms is empty (unusual) */}
          {data && data.recentTerms.length === 0 && !isLoading && (
            <EmptyState
              title={t('terminologyManagement.list.empty.title')}
              description={t('terminologyManagement.list.empty.description')}
            />
          )}
        </div>
      )}

      <CommonUsagePanel />

      <TerminologyToast messages={messages} onDismiss={dismissToast} />
    </div>
  );
}
